import { find, forEach, keyBy, keys, mapValues } from 'lodash'
import type React from 'react'
import type { Key } from 'react'
import Popover, { type PopoverProps } from '../../../dataDisplay/Popover'
import {
  ActualStockCell,
  CarersCell,
  DateTimeCell,
  EmarResidentMedicationCell,
  GenericCell,
  IconCell,
  LogCell,
  ManageDeviceCell,
  ResidentCell,
  StockLevelCell
} from '../../Table/cells'
import type { ActualStockCellProps } from '../../Table/cells/ActualStockCell'
import type { CarersCellProps } from '../../Table/cells/CarersCell'
import type { DateTimeCellProps } from '../../Table/cells/DateTimeCell'
import type { EmarResidentMedicationCellProps } from '../../Table/cells/EmarResidentMedicationCell'
import type { GenericCellProps } from '../../Table/cells/GenericCell'
import type { IconCellProps } from '../../Table/cells/IconCell'
import type { LogCellProps } from '../../Table/cells/LogCell'
import type { ManageDeviceCellProps } from '../../Table/cells/ManageDeviceCell'
import type { ResidentCellProps } from '../../Table/cells/ResidentCell'
import type { StockLevelCellProps } from '../../Table/cells/StockLevelCell'
import { type CELL_TYPES, type Column, styles } from './formatTableColumns'

export type TableCellDefaultProps = {
  key: string
  popover?: PopoverProps & {
    text?: string
  }
}

export type TableCellType = TableCellDefaultProps &
  (
    | TableCellCustom
    | ActualStockCellProps
    | DateTimeCellProps
    | IconCellProps
    | LogCellProps
    | ResidentCellProps
    | CarersCellProps
    | ManageDeviceCellProps
    | EmarResidentMedicationCellProps
    | TableCellDefault
    //
    | JSX.Element
    | string
  )

export type TableRow<
  ColumnsNames extends Key = string,
  CellType extends TableCellType = any
> = {
  key?: string
} & {
  [key in ColumnsNames]?: CellType
}
export type TableCellCustom = {
  renderCell: () => JSX.Element
}
export type TableCellCenter = string
export type TableCellDefault = GenericCellProps['data']

export const getCurrentCell = (
  cell: TableCellType,
  cellType: CELL_TYPES | undefined
) => {
  switch (cellType) {
    case 'custom': {
      const typedRowKey = cell as TableCellCustom
      return typedRowKey.renderCell && typedRowKey.renderCell()
    }
    case 'actualStock':
      return <ActualStockCell {...(cell as ActualStockCellProps)} />
    case 'dateTime':
      return <DateTimeCell {...(cell as DateTimeCellProps)} />
    case 'icon':
      return <IconCell {...(cell as IconCellProps)} />
    case 'log':
      return <LogCell {...(cell as LogCellProps)} />
    case 'emarResidentMedication':
      return (
        <EmarResidentMedicationCell
          {...(cell as EmarResidentMedicationCellProps)}
        />
      )
    case 'stockLevel':
      return <StockLevelCell {...(cell as StockLevelCellProps)} />
    case 'resident':
      return <ResidentCell {...(cell as ResidentCellProps)} />
    case 'center':
      return (
        <GenericCell
          data={{ style: styles.textCentred, value: cell as TableCellCenter }}
        />
      )
    case 'carers':
      return <CarersCell {...(cell as CarersCellProps)} />
    case 'manageDevice':
      return <ManageDeviceCell {...(cell as ManageDeviceCellProps)} />
    default:
      return <GenericCell data={cell as TableCellDefault} />
  }
}

export type ReturnedTableRow<RecordType> = {
  [key in keyof RecordType]?: (string | JSX.Element | object | number) | null
}

type CellTypes<RecordType> = {
  [index in keyof RecordType]: CELL_TYPES | undefined
}

export const getFormatedRow = <RecordType extends TableRow>(
  row: RecordType,
  cellTypes: CellTypes<RecordType>,
  columns: Column[]
) => {
  const formattedRow: ReturnedTableRow<RecordType> = {}

  // Array of all the column keys
  const columnKeys = columns.map((column) => column.key) as (
    | React.Key
    | symbol
    | undefined
  )[]

  forEach(keys(row), (key: keyof RecordType) => {
    // Checks whether the object key is one of the column keys.
    if (cellTypes[key] === 'custom') {
      const column = find(columns, { key }) as Column
      formattedRow[key] = column?.renderCell
        ? column.renderCell(row[key], row)
        : row[key].renderCell()
    } else if (columnKeys?.includes(key)) {
      formattedRow[key] = getCurrentCell(row[key], cellTypes?.[key])
    } else {
      formattedRow[key] = row[key]
    }

    if (row[key]?.popover) {
      formattedRow[key] = (
        <Popover
          content={<div>{row[key]?.popover?.text}</div>}
          {...row[key]?.popover}
        >
          {/* We are not maintaning this component anymore, so we don't really care about ts errors anymore */}
          {/* @ts-ignore */}
          <div>{formattedRow[key]}</div>
        </Popover>
      )
    }
  })

  return formattedRow
}

export const formatTableRows = <ColumnsNames extends Key = string>(
  columns: Column[],
  rows?: TableRow<ColumnsNames>[]
) => {
  // Object of column keys and cell types. e.g. { expectedStock: 'center' }
  const cellTypes = mapValues(keyBy(columns, 'key'), 'cellType') as CellTypes<
    TableRow<ColumnsNames>
  >
  const dataSource = rows?.map((row) => getFormatedRow(row, cellTypes, columns))

  return dataSource
}
